import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, TrendingUp, Users, Package, AlertTriangle } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { DashboardStatSkeleton } from '@/components/ui/skeletons';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

const Reports = () => {
  const { user } = usePermissions();

  // Fetch revenue data
  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['reports-revenue'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('total_amount, status, paid_amount, created_at')
        .eq('status', 'paid');
      if (error) throw error;
      
      const totalRevenue = data.reduce((sum, inv) => sum + (inv.paid_amount || 0), 0);
      const thisMonth = data.filter(inv => {
        const invDate = new Date(inv.created_at);
        const now = new Date();
        return invDate.getMonth() === now.getMonth() && invDate.getFullYear() === now.getFullYear();
      }).reduce((sum, inv) => sum + (inv.paid_amount || 0), 0);
      
      return { totalRevenue, thisMonth, invoiceCount: data.length };
    },
  });

  // Fetch order statistics
  const { data: orderStats, isLoading: orderLoading } = useQuery({
    queryKey: ['reports-orders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('status, total_amount, created_at');
      if (error) throw error;
      
      const totalOrders = data.length;
      const pendingOrders = data.filter(o => o.status === 'pending').length;
      const completedOrders = data.filter(o => o.status === 'complete').length;
      
      return { totalOrders, pendingOrders, completedOrders };
    },
  });

  // Fetch customer statistics
  const { data: customerStats, isLoading: customerLoading } = useQuery({
    queryKey: ['reports-customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('status, created_at');
      if (error) throw error;
      
      const activeCustomers = data.filter(c => c.status === 'active').length;
      const inactiveCustomers = data.filter(c => c.status === 'inactive').length;
      
      return { totalCustomers: data.length, activeCustomers, inactiveCustomers };
    },
  });

  // Fetch inventory statistics
  const { data: inventoryStats, isLoading: inventoryLoading } = useQuery({
    queryKey: ['reports-inventory'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('type, current_stock, min_stock_level');
      if (error) throw error;
      
      const totalProducts = data.length;
      const lowStockItems = data.filter(item => 
        item.current_stock <= (item.min_stock_level || 0)
      ).length;
      const rawMaterials = data.filter(item => item.type === 'raw_material').length;
      const finishedProducts = data.filter(item => item.type === 'finished_product').length;
      
      return { totalProducts, lowStockItems, rawMaterials, finishedProducts };
    },
  });

  const isLoading = revenueLoading || orderLoading || customerLoading || inventoryLoading;

  return (
    <AppLayout currentPage="reports">
      <div className="space-y-4 sm:space-y-6">
        <div className="space-y-1">
          <h1 className="mobile-heading">Reports & Analytics</h1>
          <p className="mobile-body text-muted-foreground">
            Business intelligence and reporting dashboard
          </p>
        </div>

        {/* Overview Cards */}
        {isLoading ? (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <DashboardStatSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="stat-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium leading-tight">Total Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl sm:text-2xl font-bold">
                  ${revenueData?.totalRevenue?.toFixed(2) || '0.00'}
                </div>
                <p className="text-xs text-muted-foreground">
                  From {revenueData?.invoiceCount || 0} paid invoices
                </p>
              </CardContent>
            </Card>
            
            <Card className="stat-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Total Orders</CardTitle>
                <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl sm:text-2xl font-bold">{orderStats?.totalOrders || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {orderStats?.completedOrders || 0} completed, {orderStats?.pendingOrders || 0} pending
                </p>
              </CardContent>
            </Card>
            
            <Card className="stat-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Active Customers</CardTitle>
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl sm:text-2xl font-bold">{customerStats?.activeCustomers || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {customerStats?.totalCustomers || 0} total customers
                </p>
              </CardContent>
            </Card>
            
            <Card className="stat-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Inventory Items</CardTitle>
                <Package className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl sm:text-2xl font-bold">{inventoryStats?.totalProducts || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {inventoryStats?.lowStockItems || 0} items low stock
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detailed Reports Section */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
          <Card className="mobile-card">
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Revenue Overview</CardTitle>
              <CardDescription className="text-sm">
                Financial performance metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Total Revenue</span>
                  <span className="text-base sm:text-lg font-bold">
                    ${revenueData?.totalRevenue?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">This Month</span>
                  <span className="text-base sm:text-lg font-bold">
                    ${revenueData?.thisMonth?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Paid Invoices</span>
                  <span className="text-base sm:text-lg font-bold">{revenueData?.invoiceCount || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mobile-card">
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Inventory Status</CardTitle>
              <CardDescription className="text-sm">
                Current stock levels and alerts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Total Products</span>
                  <span className="text-base sm:text-lg font-bold">{inventoryStats?.totalProducts || 0}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Raw Materials</span>
                  <span className="text-base sm:text-lg font-bold">{inventoryStats?.rawMaterials || 0}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Finished Products</span>
                  <span className="text-base sm:text-lg font-bold">{inventoryStats?.finishedProducts || 0}</span>
                </div>
                {(inventoryStats?.lowStockItems || 0) > 0 && (
                  <div className="flex items-center gap-2 p-2 sm:p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200 dark:border-orange-800">
                    <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600 shrink-0" />
                    <span className="text-xs sm:text-sm font-medium text-orange-600">
                      {inventoryStats?.lowStockItems} items need restocking
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer & Order Analytics */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
          <Card className="mobile-card">
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Customer Insights</CardTitle>
              <CardDescription className="text-sm">
                Customer base analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Total Customers</span>
                  <span className="text-base sm:text-lg font-bold">{customerStats?.totalCustomers || 0}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Active</span>
                  <span className="text-base sm:text-lg font-bold text-green-600">
                    {customerStats?.activeCustomers || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Inactive</span>
                  <span className="text-base sm:text-lg font-bold text-muted-foreground">
                    {customerStats?.inactiveCustomers || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mobile-card">
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Order Analytics</CardTitle>
              <CardDescription className="text-sm">
                Order fulfillment metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Total Orders</span>
                  <span className="text-base sm:text-lg font-bold">{orderStats?.totalOrders || 0}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Completed</span>
                  <span className="text-base sm:text-lg font-bold text-green-600">
                    {orderStats?.completedOrders || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-xs sm:text-sm font-medium">Pending</span>
                  <span className="text-base sm:text-lg font-bold text-orange-600">
                    {orderStats?.pendingOrders || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default Reports;